import { Button, Eyebrow, Icon } from '@beacon/ui/components';
import { useEffect, useRef } from 'react';
import type { TooltipRenderProps } from 'react-joyride';
import type { BeaconStepData } from './types';

// branded replacement for react-joyride's default tooltip
export function TourTooltip({
  backProps,
  closeProps,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  const data = (step.data ?? {}) as BeaconStepData;
  const isModal = data.kind === 'modal';
  const isClickAdvance = data.advance === 'click';
  const skipLabel = data.skipLabel ?? 'Skip tour';
  const primaryLabel = data.primaryLabel ?? (isLastStep ? 'Done' : 'Next');
  const eyebrow = isModal
    ? data.eyebrow
    : data.stepNumber && data.stepTotal
      ? `STEP ${data.stepNumber} OF ${data.stepTotal}`
      : undefined;

  // make the modal the focus on load for keyboard nav
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isModal) primaryRef.current?.focus({ preventScroll: true });
  }, [isModal, index]);

  return (
    <div
      {...tooltipProps}
      className={`bg-surface-1 border-stroke-2 rounded-lg border shadow-2xl ${
        isModal ? 'w-[520px] max-w-[calc(100vw-32px)] p-6' : 'w-[320px] p-4'
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        {eyebrow ? <Eyebrow primary={isModal}>{eyebrow}</Eyebrow> : <span />}
        <button
          {...closeProps}
          className="text-text-3 hover:text-text-1 -mt-1 -mr-1 shrink-0 cursor-pointer transition-colors"
        >
          <Icon symbol="close-line" className="h-4 w-4" />
        </button>
      </div>

      {step.title && (
        <div className={`text-text-1 font-semibold tracking-[-0.01em] ${isModal ? 'mb-2 text-lg' : 'mb-1.5 text-sm'}`}>
          {step.title}
        </div>
      )}
      <div className={`text-text-2 leading-relaxed text-pretty ${isModal ? 'text-md' : 'text-[13px]'}`}>
        {step.content}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        {skipLabel ? (
          <button {...skipProps} className="text-text-3 hover:text-text-2 cursor-pointer text-xs transition-colors">
            {skipLabel}
          </button>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          {!isModal && index > 1 && (
            <Button {...backProps} variant="outline" size="sm">
              Back
            </Button>
          )}

          {isClickAdvance ? (
            <span className="text-primary-9 flex items-center gap-1.5 text-xs font-medium">
              <Icon symbol="cursor-line" className="h-3.5 w-3.5" />
              {data.waitHint}
            </span>
          ) : (
            <Button ref={primaryRef} {...primaryProps} variant="primary" size="sm">
              {primaryLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
